import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingMessage, SupplyPortal } from "../../../../components";
import { ItemsSliderBaseModalProps } from "../../../Items/types";
import { useDataTableContext } from "../../../../hooks";
import {
	slideUpModalOverlayVariants,
	slideUpModalVariants,
} from "../../../../motionVariants";
import { useLocation, useNavigate } from "react-router-dom";
import {
	getUnitDetails,
	getSalesByUnit,
	getAcquisitionByUnit,
	getReceiptByUnit,
	getFulfillmentByUnit,
} from "../../api/InventoryApi";
import InventoryUnitsViewModalContent from "./InventoryUnitsViewModalContent";

const InventoryUnitsViewModal = <T,>({
	isOpen,
	onClose,
	hasBorder = false,
	borderColor = "primary",
	loadingMessage = "",
}: ItemsSliderBaseModalProps<T>) => {
	const location = useLocation();
	const navigate = useNavigate();
	const modalRef = useRef<HTMLDivElement>(null);
	const [unitDetails, setUnitDetails] = useState<any>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { setIsNestedChildTableModalToggle } = useDataTableContext();

	const [unitsUuidFromUrl, setUnitsUuidFromUrl] = useState<string | null>(
		() => {
			const params = new URLSearchParams(window.location.search);
			return params.get("uuid");
		}
	);

	useEffect(() => {
		let prevSearch = window.location.search;

		const interval = setInterval(() => {
			if (window.location.search !== prevSearch) {
				prevSearch = window.location.search;
				const params = new URLSearchParams(window.location.search);
				setUnitsUuidFromUrl(params.get("uuid"));
			}
		}, 100); // adjust if needed

		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		if (!isOpen) return;

		if (!unitsUuidFromUrl) {
			setUnitDetails(null);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		async function fetchUnitData() {
			try {
				const [
					unitDetailsData,
					unitSalesData,
					unitAcquisitionData,
					unitFulfillmentData,
					unitReceiptData,
				] = await Promise.all([
					getUnitDetails(unitsUuidFromUrl as any),
					getSalesByUnit(unitsUuidFromUrl as any),
					getAcquisitionByUnit(unitsUuidFromUrl as any),
					getFulfillmentByUnit(unitsUuidFromUrl as any),
					getReceiptByUnit(unitsUuidFromUrl as any),
				]);

				setUnitDetails({
					OVERVIEW: unitDetailsData,
					SALES: unitSalesData[0],
					ACQUISITION: unitAcquisitionData[0],
					RECEIPT: unitReceiptData[0],
					FULFILLMENT: unitFulfillmentData[0],
				});
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		}

		fetchUnitData();
	}, [isOpen, unitsUuidFromUrl]);

	const handleClose = () => {
		localStorage.removeItem("uuid");
		const params = new URLSearchParams(location.search);
		params.delete("uuid");
		const newSearch = params.toString();

		navigate({
			pathname: location.pathname,
			search: newSearch ? `?${newSearch}` : "",
		});
		setIsNestedChildTableModalToggle(false);
		onClose();
	};

	if (!isOpen) return null;

	const modalBoxClasses = `
    modal-box
    w-full max-w-[1200px]
    rounded-t-3xl
    bg-white
    p-6
    min-h-[110vh]
    overflow-auto
    ${hasBorder ? `border border-${borderColor}` : ""}
  `;

	return (
		<SupplyPortal containerId="portal-root">
			<AnimatePresence>
				{/* BACKDROP: clicking here closes */}
				<motion.div
					key="inventory-backdrop"
					className="fixed inset-0 bg-black/50 z-[9910]"
					initial={slideUpModalOverlayVariants.initial}
					animate={slideUpModalOverlayVariants.animate}
					exit={slideUpModalOverlayVariants.exit}
					transition={slideUpModalOverlayVariants.transition}
				/>

				{/* SHEET: stop propagation so clicks inside don’t close */}
				<motion.div
					key="inventory-sheet"
					className="fixed inset-x-0 top-0 bottom-0 z-[9920] flex justify-center"
					initial={{ y: "100%", opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: "100%", opacity: 0 }}
					transition={slideUpModalVariants.transition}
					onClick={handleClose}
				>
					<div className="absolute inset-x-4 bottom-0 top-0 flex justify-center">
						<div
							ref={modalRef}
							className={modalBoxClasses}
							onClick={(e) => e.stopPropagation()}
						>
							{isLoading ? (
								<div className="flex h-full items-center justify-center">
									<LoadingMessage message={loadingMessage} />
								</div>
							) : (
								<InventoryUnitsViewModalContent
									modalRef={modalRef}
									parentData={unitDetails}
									pageTitle=""
									onClose={handleClose}
									setIsLoading={setIsLoading}
									parentSelectsData={undefined}
								/>
							)}
						</div>
					</div>
				</motion.div>
			</AnimatePresence>
		</SupplyPortal>
	);
};

export default InventoryUnitsViewModal;
